import moment from "moment";
import React, {useState} from "react";
import styled from "styled-components";
import {UserIcon} from "ui/icons";
import {defaultThemeColors, fonts} from "ui/theme";
import {ButtonPrimary} from "../Button/ButtonStyled";
import {ClassDateCard} from "../dateCard/ClassDateCard";
import {Avatar} from "../editAvatar/Avatar";

require("moment-timezone");

const getTimeAfter48Hours = (class_date, class_time) => {
    const classDateTime = moment(
        `${class_date} ${class_time}`,
        "YYYY-MM-DD HH:mm:ss"
    );
    return classDateTime.isValid() ? classDateTime.add(48, "hours") : null;
};

export const DEFAULT_TIMEZONE = "America/New_York";

export const CancelledClassCard = ({cancelledClass, onReview, onReport, onProfile, onRehire}) => {
    const [isOpen, setIsOpen] = useState(false);

    const momentDateTime = moment.tz(
        `${cancelledClass?.class_date} ${cancelledClass?.class_time}`,
        "YYYY-MM-DD HH:mm:ss",
        cancelledClass?.time_zone || DEFAULT_TIMEZONE
    );

    const timeAfter48Hours = getTimeAfter48Hours(
        cancelledClass.class_date,
        cancelledClass.class_time
    );

    const showReportIssue = timeAfter48Hours
        ? moment().isBefore(timeAfter48Hours)
        : false;

    const class_type = cancelledClass?.class_type
        ? cancelledClass?.class_type.toLowerCase()
        : "";

    const isOnline = cancelledClass?.online_type === "online"

    const badgeVariant = isOnline ? "black_fade_small" : class_type === "private" ? "primary_fade_small" : "destructive_fade_small"

    return (
        <ClassList>
            <ClassItem>
                <ClassInfo>
                    <Avatar
                        onClick={onProfile}
                        profile={cancelledClass?.teacher_profile_pic}
                        alt={cancelledClass?.teacher_first_name}
                    />

                    <InfoContainer>
                        <Info>
                            <ClassType isMobile>
                                <ButtonPrimary
                                    variant={badgeVariant}
                                    style={{
                                        pointerEvents: "none",
                                    }}
                                >
                                    {isOnline ? "Online" : class_type}
                                </ButtonPrimary>
                            </ClassType>
                            <Name>{cancelledClass?.teacher_first_name}</Name>
                            <Details>
                                <span>
                                    <UserIcon color="placeholder" width="15" height="15"/>
                                </span>
                                <span>{cancelledClass?.student_per_class}</span> |{" "}
                                <span>{cancelledClass?.class}</span>
                            </Details>
                        </Info>
                        <ClassType>
                            <ButtonPrimary
                                variant={badgeVariant}
                                style={{
                                    pointerEvents: "none",
                                }}
                            >
                                {isOnline ? "Online" : class_type}
                            </ButtonPrimary>
                        </ClassType>
                        <ActionButton onClick={() => setIsOpen((prev) => !prev)}>
                            <ArrowDown isOpen={isOpen}/>
                        </ActionButton>
                    </InfoContainer>
                </ClassInfo>
                <ExpandedDetails isOpen={isOpen}>
                    <ClassDateCard
                        priceBreakdown={{
                            booking_total_amount: cancelledClass?.booking_total_amount,
                            discount_amount: cancelledClass?.discount_amount,
                            teache_fee: cancelledClass?.teache_fee,
                            tax: cancelledClass?.tax,
                            total_amount: cancelledClass?.total_amount,
                            refund_amount: cancelledClass?.refund_amount,
                        }}
                        date={{
                            type: cancelledClass?.class_type,
                            date: momentDateTime.format("MMM D, YYYY"),
                            time: momentDateTime.format("h:mm a"),
                            cost: cancelledClass?.total_amount,
                        }}
                        isOnline={isOnline}
                    />

                    <Actions>
                        {onRehire && <ButtonPrimary onClick={onRehire}>Hire me</ButtonPrimary>}
                    </Actions>
                </ExpandedDetails>
            </ClassItem>
        </ClassList>
    );
};

const ClassList = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    width: 100%;
`;

const ClassItem = styled.div`
    border-width: 2px;
    border-style: solid;
    border-color: ${defaultThemeColors.lightBlue};
    background-color: ${defaultThemeColors.white};
    border-radius: 8px;
    overflow: hidden;
    padding: 1.5rem;
    width: 100%;
`;

const ClassInfo = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
`;

const ClassType = styled.div`
    display: ${({isMobile}) => (isMobile ? "none" : "block")};

    @media (max-width: 768px) {
        display: ${({isMobile}) => (isMobile ? "block" : "none")};
    }
`;

const InfoContainer = styled.div`
    display: flex;
    justify-content: space-between;
    flex: 1;
`;

const Info = styled.div`
    flex: 1;
    display: grid;
    gap: 0.5rem;
`;

const Name = styled.div`
    font-size: ${fonts.sizes.regular};
    font-weight: bold;
`;

const Details = styled.div`
    font-size: ${fonts.sizes.small};
    color: ${defaultThemeColors.placeholder};
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 0.3rem;

    & span:nth-of-type(2) {
        color: ${defaultThemeColors.txt};
    }
`;

const ExpandedDetails = styled.div`
    padding-top: 1rem;
    display: grid;
    gap: 0.5rem;

    visibility: visible;

    @media (max-width: 768px) {
        visibility: ${({isOpen}) => (isOpen ? "visible" : "hidden")};
        max-height: ${({isOpen}) => (isOpen ? "50rem" : "0rem")};
        overflow: hidden;
        transition: max-height 0.3s ease-in-out, opacity 0.3s ease-in-out;

        opacity: ${({isOpen}) => (isOpen ? 1 : 0)};
    }
`;

const Actions = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.6rem;

    > * {
        grid-column: span 1;
    }

    &:has(> *:nth-child(3)) > :first-child {
        grid-column: span 2;
    }

    > :only-child {
        grid-column: span 2;
    }
`;

const ActionButton = styled.div`
    align-self: center;

    display: none;

    @media (max-width: 1024px) {
        display: block;
    }
`;

const ArrowDown = (props) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={20}
        height={20}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        style={{
            transform: props.isOpen ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.3s ease",
        }}
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        {...props}
    >
        <path d="M12 5v14"/>
        <path d="m19 12-7 7-7-7"/>
    </svg>
);
